import type { BillingTier } from "schemas/generated/billing";

import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { z } from "zod";

import { addOnProductUnitNameSchema } from "./addOns";

const unpaidBillingTierSchema = z.enum(["free"]);

export const paidBillingTierSchema = z
  .enum(["starter", "pro"])
  .describe("PaidBillingTier");

export const billingTierSchema = z
  .union([unpaidBillingTierSchema, paidBillingTierSchema])
  .describe("BillingTier");

export const BillingTiers: Record<Uppercase<BillingTier>, BillingTier> = {
  FREE: "free",
  STARTER: "starter",
  PRO: "pro",
};

export const productUnitNameSchema = billingTierSchema
  .or(addOnProductUnitNameSchema)
  .describe("ProductUnitName");

export const paymentProcessorSchema = z
  .enum(["shopify", "stripe", "none"])
  .describe("PaymentProcessor");

export const billingIntervalSchema = z
  .enum(["monthly", "yearly"])
  .describe("BillingInterval");

export const meterEventNameSchema = z
  .enum(["userMessages"])
  .describe("MeterEventName");

const productUnitSchema = z
  .object({
    id: z.string().uuid(),
    startedAt: z.date(),
    endedAt: z.date().nullable(),
    monthlyAmount: z.number(),
    yearlyAmount: z.number(),
    name: productUnitNameSchema,
  })
  .describe("ProductUnit");

export const subscriptionDetailsSchema = z
  .object({
    id: z.uuid(),
    paymentProcessor: paymentProcessorSchema,
    paymentProcessorSubscriptionId: z.string().nullable(),
    startedAt: z.date(),
    endedAt: z.date().nullable(),
    billingInterval: billingIntervalSchema,
    isManagedByReplo: z.boolean(),
    billingTier: billingTierSchema,
    stripeCustomerId: z.string().nullable(),
    productUnits: z.array(productUnitSchema),
    paymentFailedAt: z.date().nullable(),
  })
  .describe("SubscriptionDetails");

export type PlanEntitlements = {
  maxShopifyThemeProjects: number | null;
  maxReploSites: number | null;
  maxMonthlyChatMessages: number | null;
  maxCustomDomains: number | null;
  maxPublishedElements: number | null;
  maxExperiments: number | null;
};

export type PlanInfo = {
  tier: BillingTier;
  displayName: string;
  description: string;
  monthlyPrice: number;
  yearlyPricePerMonth: number;
} & PlanEntitlements;

const FREE_PLAN_INFO: PlanInfo = {
  tier: "free",
  displayName: "Free",
  description: "Publish your site to a Replo subdomain.",
  monthlyPrice: 0,
  yearlyPricePerMonth: 0,
  // NOTE (Andrew 2026-01-20 REPL-22339):
  // Free plan users aren't allowed to perform actions that would make their projects paid.
  // See: https://github.com/replohq/andytown/pull/17774#discussion_r2710344479.
  maxShopifyThemeProjects: 0,
  maxReploSites: 0,
  maxMonthlyChatMessages: 100,
  maxCustomDomains: 0,
  maxPublishedElements: 0,
  maxExperiments: 0,
};

const STARTER_PLAN_INFO: PlanInfo = {
  tier: "starter",
  displayName: "Starter",
  description: "Best for entrepreneurs and small teams.",
  monthlyPrice: 119,
  yearlyPricePerMonth: 99,
  maxShopifyThemeProjects: 1,
  maxReploSites: 1,
  maxMonthlyChatMessages: 1000,
  maxCustomDomains: null,
  maxPublishedElements: 15,
  maxExperiments: null,
};

const PRO_PLAN_INFO: PlanInfo = {
  tier: "pro",
  displayName: "Pro",
  description: "Best for agencies and high-scale teams.",
  monthlyPrice: 599,
  yearlyPricePerMonth: 499,
  maxShopifyThemeProjects: 1,
  maxReploSites: 1,
  maxMonthlyChatMessages: null,
  maxCustomDomains: null,
  maxPublishedElements: null,
  maxExperiments: null,
};

export const getBillingPlanInfoByTier = (tier: BillingTier): PlanInfo => {
  return exhaustiveSwitch({ type: tier })({
    free: () => FREE_PLAN_INFO,
    starter: () => STARTER_PLAN_INFO,
    pro: () => PRO_PLAN_INFO,
  });
};

export const stripeSubscriptionMetadataKeySchemas = {
  isManagedByReplo: z.enum(["true", "false"]),
};

export const stripeSubscriptionMetadataSchema = z
  .object({
    isManagedByReplo:
      stripeSubscriptionMetadataKeySchemas.isManagedByReplo.optional(),
  })
  .describe("StripeSubscriptionMetadata");

export const BILLING_PLAN_CONFIRMATION_PARAM = "billing-confirmation";
export const BILLING_PLAN_CONFIRMATION_TYPE_PARAM = "confirmation-type";
